/** Shared tuning constants. Both client and server must agree on these. */
/** Max clients per room instance before Colyseus spins up a sibling room. */
export const MAX_CLIENTS_PER_ROOM = 150;
/** Server simulation rate (Hz). */
export const SERVER_TICK_HZ = 20;
/** Rate at which the server flushes state patches to clients (Hz). */
export const PATCH_RATE_HZ = 15;
/** Max input messages a client may send per second. */
export const INPUT_SEND_HZ = 15;
/** Player movement speed in pixels per second (authoritative). */
export const PLAYER_SPEED = 140;
/** Tolerance multiplier used by the server's anti-cheat speed check. */
export const SPEED_TOLERANCE = 1.15;
/** Grace period (ms) before a disconnected player is removed from the room. */
export const RECONNECT_GRACE_MS = 10_000;
/** Max characters for a display name. */
export const MAX_NAME_LENGTH = 20;
/** Max characters for a tribute message. */
export const MAX_TRIBUTE_LENGTH = 120;
/** Gates open this long before the performance starts. */
export const GATES_OPEN_LEAD_SEC = 3600;
/** Length of the opening ceremony, from the show start epoch. */
export const OPENING_CEREMONY_SEC = 300;
/**
 * The moment of silence, immediately after the opening ceremony.
 *
 * Two minutes. Long enough to be a silence rather than a pause, short enough
 * that a crowd on phones holds it.
 */
export const MOMENT_OF_SILENCE_SEC = 120;
/** Closing Mayabini occupies the last stretch of the runtime. */
export const CLOSING_MAYABINI_SEC = 360;
/** Seconds from the show start epoch to the first frame of the tribute video. */
export const VIDEO_PREROLL_SEC = OPENING_CEREMONY_SEC + MOMENT_OF_SILENCE_SEC;
/** Playback drift (seconds) above which a client re-seeks the stage video. */
export const MAX_VIDEO_DRIFT_SEC = 2;
/** How often (ms) a client checks stage video drift. */
export const VIDEO_DRIFT_CHECK_MS = 30_000;
/** Emote rate limit: minimum ms between emotes per player. */
export const EMOTE_COOLDOWN_MS = 1000;
/** Max diyas rendered per room before the oldest fade out. */
export const MAX_DIYAS_PER_ROOM = 500;
/** Max flowers laid per room before the oldest are cleared away. */
export const MAX_FLOWERS_PER_ROOM = 400;
/** Offering a flower is deliberate, like lighting a diya. */
export const FLOWER_COOLDOWN_MS = 2000;
/** Distinct blooms in the Phool Arpan offering, chosen at random per flower. */
export const FLOWER_KINDS = 4;
/** How long one raised voice counts toward the pavilion's chorus. */
export const SING_DURATION_MS = 4000;
/** Minimum gap between raising your voice again. */
export const SING_COOLDOWN_MS = 1500;
/**
 * The Radio Garden's stations.
 *
 * Ids only. What each one actually plays is an environment decision, exactly
 * like the tribute video — the rights posture for archival audio is not
 * settled, and it must never be a code change to swap a track.
 *
 * Five bands, chosen the way an Assamese listener would divide his catalogue
 * rather than the way a Western one would: Bihu, geet, his Hindi work, bhakti,
 * and Borgeet. The old dial had "Rock" and "In his words" — the first was a
 * category nobody asked the radio for, and the second never had a recording
 * behind it, so it sat on the dial all evening playing silence.
 */
export const RADIO_STATIONS = ["bihu", "geet", "hindi", "bhokti", "borgeet"];
/**
 * A fifth position on the dial: whatever Zubeen Radio is playing right now.
 *
 * Deliberately not in `RADIO_STATIONS`. That list is the venue's own content,
 * which exists on 19 September whether or not the module is ever deployed, and
 * every venue-side lookup — `stationUrl`, the station labels — is written
 * against it. This id has no URL of its own: the module supplies one per slot.
 *
 * The server still has to know it, because the dial is server-held so that
 * everyone standing in the garden hears the same thing. A client-only station
 * would be refused as an unknown id, which is exactly what happened the first
 * time this was wired up.
 */
export const MODULE_RADIO_STATION = "zubeen-radio";
/** Every id the server will accept for the garden's dial. */
export function isKnownStation(id) {
    return id === MODULE_RADIO_STATION || RADIO_STATIONS.includes(id);
}
/** How close two people must be to ask to talk, in pixels. */
export const VOICE_RANGE = 160;
/** An unanswered talk request expires rather than ringing forever. */
export const VOICE_INVITE_TIMEOUT_MS = 20_000;
/** Available avatar sprite ids. */
/**
 * Who a fan can arrive as.
 *
 * Three men and three women. It was four, and all four were the SAME man in
 * four shades of cloth — which at venue scale is one man copy-pasted, because
 * a shade of white is not something you can pick out of a crowd at forty
 * pixels tall. A second body reads instantly at any size, and it is also
 * simply closer to who will be there.
 *
 * Everything else follows this list: the sheets that get preloaded, the
 * animations, the picker on the gate, and the server's check that an arriving
 * fan asked for one that exists. Adding a seventh is this line and a sheet.
 */
export const AVATAR_IDS = [
    "avatar1",
    "avatar2",
    "avatar3",
    "avatar4",
    "avatar5",
    "avatar6",
];
/** Emote ids available in the reaction bar. */
export const EMOTE_IDS = ["pray", "heart", "diya", "music", "tear", "clap"];
//# sourceMappingURL=constants.js.map